import { useState, useRef, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { supabasePartner } from '../../lib/supabase';
import { labelBase, borderBase, inputBase } from '../../components/shared/helpers';

// Indian states / UTs with their GST state codes. Selecting a state derives its
// two-digit code automatically (used for the GSTIN's leading state digits).
// Sorted alphabetically by name for the dropdown.
const STATES = [
  { name: 'Andaman and Nicobar Islands', code: '35' },
  { name: 'Andhra Pradesh', code: '37' },
  { name: 'Arunachal Pradesh', code: '12' },
  { name: 'Assam', code: '18' },
  { name: 'Bihar', code: '10' },
  { name: 'Chandigarh', code: '04' },
  { name: 'Chhattisgarh', code: '22' },
  { name: 'Dadra and Nagar Haveli and Daman and Diu', code: '26' },
  { name: 'Delhi', code: '07' },
  { name: 'Goa', code: '30' },
  { name: 'Gujarat', code: '24' },
  { name: 'Haryana', code: '06' },
  { name: 'Himachal Pradesh', code: '02' },
  { name: 'Jammu and Kashmir', code: '01' },
  { name: 'Jharkhand', code: '20' },
  { name: 'Karnataka', code: '29' },
  { name: 'Kerala', code: '32' },
  { name: 'Ladakh', code: '38' },
  { name: 'Lakshadweep', code: '31' },
  { name: 'Madhya Pradesh', code: '23' },
  { name: 'Maharashtra', code: '27' },
  { name: 'Manipur', code: '14' },
  { name: 'Meghalaya', code: '17' },
  { name: 'Mizoram', code: '15' },
  { name: 'Nagaland', code: '13' },
  { name: 'Odisha', code: '21' },
  { name: 'Puducherry', code: '34' },
  { name: 'Punjab', code: '03' },
  { name: 'Rajasthan', code: '08' },
  { name: 'Sikkim', code: '11' },
  { name: 'Tamil Nadu', code: '33' },
  { name: 'Telangana', code: '36' },
  { name: 'Tripura', code: '16' },
  { name: 'Uttar Pradesh', code: '09' },
  { name: 'Uttarakhand', code: '05' },
  { name: 'West Bengal', code: '19' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

// Registration progress steps.
// `full` shows on wider cards, `short` on narrow (mobile) screens.
const STEPS = [
  { n: 1, full: 'Account & Company', short: 'Account' },
  { n: 2, full: 'GST Details', short: 'GST' },
  { n: 3, full: 'Documents', short: 'Documents' },
];

// Document upload constraints — kept in sync with the partner-upload edge function.
const MAX_DOC_BYTES = 3 * 1024 * 1024; // 3MB
const ALLOWED_DOC_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

// Client-side pre-flight check (the edge function re-validates server-side).
// Returns an error string, or '' when the file is acceptable.
function validateDocFile(file) {
  if (!ALLOWED_DOC_TYPES.includes(file.type)) return 'File must be a PDF, JPG, or PNG.';
  if (file.size > MAX_DOC_BYTES) return 'File must be 3MB or smaller.';
  return '';
}

// Read a File as a base64 data URL. partner-upload strips the data: prefix.
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

// Compact horizontal stepper. `current` is the active step number (1-based).
// Steps below `current` render as done (gold fill + check), the current step is
// highlighted (gold ring), and steps above are muted/dimmed.
function Stepper({ current }) {
  return (
    <div className="flex items-start mb-7" aria-label={`Step ${current} of ${STEPS.length}`}>
      {STEPS.map((s, i) => {
        const done = s.n < current;
        const active = s.n === current;
        const circle = done
          ? 'bg-[#C9A84C] border-[#C9A84C] text-[#0A1628]'
          : active
            ? 'bg-transparent border-[#C9A84C] text-[#C9A84C] shadow-[0_0_0_3px_rgba(201,168,76,0.15)]'
            : 'bg-transparent border-white/15 text-white/35';
        const label = done || active ? 'text-white/80' : 'text-white/35';
        return (
          <Fragment key={s.n}>
            {/* Connector before every step except the first. Equal flex-1 length
                on both sides keeps the three steps evenly distributed. Gold once
                the preceding step is done (i.e. once we've reached this step). */}
            {i > 0 && (
              <div
                className={`flex-1 h-px mt-3.5 transition-colors duration-300 ${
                  s.n <= current ? 'bg-[#C9A84C]' : 'bg-white/12'
                }`}
              />
            )}
            {/* Fixed, equal-width step column so connectors stay symmetric
                regardless of label length; label centered under the circle. */}
            <div className="flex flex-col items-center gap-1.5 w-[68px] shrink-0">
              <div
                className={`flex items-center justify-center w-7 h-7 rounded-full border font-dm-sans text-[12px] font-medium transition-colors duration-300 ${circle}`}
              >
                {done ? (
                  <svg viewBox="0 0 20 20" fill="none" className="w-3.5 h-3.5" aria-hidden="true">
                    <path d="M4 10.5l3.5 3.5L16 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  s.n
                )}
              </div>
              <span className={`font-dm-sans text-[10.5px] tracking-[0.04em] leading-tight text-center transition-colors duration-300 ${label}`}>
                <span className="hidden sm:inline">{s.full}</span>
                <span className="sm:hidden">{s.short}</span>
              </span>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}

const cardWrap =
  'w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter';
const cardInner = 'bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8';
const primaryBtn =
  'w-full py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:bg-[#B8973E] disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100 transition-[transform,background-color,opacity] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]';
const ghostBtn =
  'py-[14px] px-6 border border-[rgba(212,168,67,0.4)] text-[#D4A843] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:border-[rgba(212,168,67,0.7)] hover:bg-[rgba(212,168,67,0.06)] transition-[transform,border-color,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)]';
const errText = 'font-dm-sans text-sm text-white/55 font-light leading-relaxed';
// Muted per-field helper line — matches the existing password/GSTIN hint style.
const hintText = 'font-dm-sans text-[13px] text-white/40 font-light leading-relaxed';

// Empty-state file picker button (dashed outline) and the selected-file chip.
const pickerBtn =
  'flex items-center justify-center gap-2 w-full py-[13px] rounded-xl border border-dashed border-white/20 text-white/55 font-dm-sans text-[13px] hover:border-[rgba(212,168,67,0.5)] hover:text-white/80 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]';

// A single document picker. Selection/validation lives in the parent; this
// only renders the chosen file (with a Remove control) or a "Choose file"
// affordance, and forwards the picked File up via onSelect.
function DocPicker({ label, hint, file, error, onSelect, onRemove }) {
  const inputRef = useRef(null);
  const openPicker = () => inputRef.current?.click();
  const clearInput = () => {
    if (inputRef.current) inputRef.current.value = '';
  };
  return (
    <div className="group flex flex-col gap-2">
      <label className={labelBase}>{label}</label>
      {file ? (
        <div className="flex items-center gap-3 rounded-xl border border-[rgba(212,168,67,0.4)] bg-[rgba(212,168,67,0.06)] px-4 py-[11px]">
          <svg viewBox="0 0 20 20" fill="none" className="w-4 h-4 shrink-0 text-[#D4A843]" aria-hidden="true">
            <path d="M11.5 2.5H5.5A1.5 1.5 0 004 4v12a1.5 1.5 0 001.5 1.5h9A1.5 1.5 0 0016 16V7l-4.5-4.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
            <path d="M11.5 2.5V7H16" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
          </svg>
          <span className="flex-1 min-w-0 truncate font-dm-sans text-sm text-white/80">{file.name}</span>
          <button
            type="button"
            onClick={() => { clearInput(); onRemove(); }}
            className="shrink-0 font-dm-sans text-[12px] tracking-[0.04em] text-white/45 hover:text-white/80 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] rounded"
          >
            Remove
          </button>
        </div>
      ) : (
        <button type="button" onClick={openPicker} className={pickerBtn}>
          <svg viewBox="0 0 20 20" fill="none" className="w-4 h-4" aria-hidden="true">
            <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          Choose file
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="hidden"
        onChange={e => {
          const f = e.target.files?.[0];
          // Reset so re-selecting the same file still fires onChange next time.
          clearInput();
          if (f) onSelect(f);
        }}
      />
      {error ? <p className={errText}>{error}</p> : <p className={hintText}>{hint}</p>}
    </div>
  );
}

export default function PartnerRegister() {
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false); // true once registration succeeds → check-email screen

  // Step 1 — account & company
  const [companyName, setCompanyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Step 2 — GST details
  const [gstin, setGstin] = useState('');
  const [gstLegalName, setGstLegalName] = useState('');
  const [gstAddress, setGstAddress] = useState('');
  const [pincode, setPincode] = useState('');
  const [state, setState] = useState('');
  const stateCode = STATES.find(s => s.name === state)?.code || '';

  // Step 3 — documents
  const [gstCertFile, setGstCertFile] = useState(null);
  const [gstCertError, setGstCertError] = useState('');
  const [tradeLicenseFile, setTradeLicenseFile] = useState(null);
  const [tradeLicenseError, setTradeLicenseError] = useState('');

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Non-fatal note shown on the check-email screen when doc upload didn't land.
  const [uploadNote, setUploadNote] = useState('');

  // ─── Document selection handlers (validate before accepting) ────────────────
  const handleGstCertSelect = (file) => {
    const err = validateDocFile(file);
    if (err) { setGstCertError(err); setGstCertFile(null); return; }
    setGstCertError('');
    setGstCertFile(file);
  };
  const handleTradeLicenseSelect = (file) => {
    const err = validateDocFile(file);
    if (err) { setTradeLicenseError(err); setTradeLicenseFile(null); return; }
    setTradeLicenseError('');
    setTradeLicenseFile(file);
  };

  // ─── Validity (drives disabled state on Next / Create account) ──────────────
  const step1Valid =
    companyName.trim() &&
    contactPerson.trim() &&
    EMAIL_RE.test(email.trim()) &&
    phone.trim().length > 0 &&
    password.length >= 6 &&
    confirmPassword === password;

  const step2Valid =
    GSTIN_RE.test(gstin.trim()) &&
    gstLegalName.trim() &&
    gstAddress.trim() &&
    /^\d{6}$/.test(pincode.trim()) &&
    state.trim();

  // Step 3 only needs the GST certificate; the trade license is optional.
  const step3Valid = !!gstCertFile;

  // ─── Step 1 → Step 2 ────────────────────────────────────────────────────────
  const handleNext = () => {
    const next = {};
    if (!companyName.trim()) next.companyName = 'Company name is required';
    if (!contactPerson.trim()) next.contactPerson = 'Contact person is required';
    if (!EMAIL_RE.test(email.trim())) next.email = 'Please enter a valid email address';
    if (!phone.trim()) next.phone = 'Please enter a phone number';
    if (password.length < 6) next.password = 'Password must be at least 6 characters';
    if (confirmPassword !== password) next.confirmPassword = 'Passwords do not match';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSubmitError('');
    setStep(2);
  };

  // ─── Step 2 → Step 3 (collect only; account is created at Step 3) ───────────
  const handleNext2 = () => {
    const next = {};
    if (!GSTIN_RE.test(gstin.trim())) next.gstin = 'Enter a valid 15-character GSTIN';
    if (!gstLegalName.trim()) next.gstLegalName = 'GST legal name is required';
    if (!gstAddress.trim()) next.gstAddress = 'GST address is required';
    if (!/^\d{6}$/.test(pincode.trim())) next.pincode = 'Enter a valid 6-digit pincode';
    if (!state.trim()) next.state = 'Please select a state';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSubmitError('');
    setStep(3);
  };

  // Step-aware back navigation, preserving all collected data.
  const handleBack = () => {
    setSubmitError('');
    setStep(s => Math.max(1, s - 1));
  };

  // ─── Submit orchestration (fires at Step 3): ────────────────────────────────
  //     signUp → partner-register → partner-upload → check-email.
  // Documents are converted to base64 up front; the upload step is NON-FATAL —
  // once the account + partner row exist, a failed upload only adds a soft note.
  const handleSubmit = async () => {
    if (!gstCertFile) {
      setGstCertError('Please select your GST certificate.');
      return;
    }

    const cleanEmail = email.trim().toLowerCase();
    const dupMsg = 'An account with this email already exists. Please log in or reset your password.';

    setSubmitError('');
    setUploadNote('');
    setSubmitting(true);
    try {
      // 0. Read documents into base64 data URLs (partner-upload strips the prefix).
      let gstCertPayload;
      let tradeLicensePayload = null;
      try {
        gstCertPayload = {
          data: await fileToDataUrl(gstCertFile),
          filename: gstCertFile.name,
          mimeType: gstCertFile.type,
        };
        if (tradeLicenseFile) {
          tradeLicensePayload = {
            data: await fileToDataUrl(tradeLicenseFile),
            filename: tradeLicenseFile.name,
            mimeType: tradeLicenseFile.type,
          };
        }
      } catch (_) {
        setSubmitError('Could not read your documents. Please re-select them and try again.');
        setSubmitting(false);
        return;
      }

      // 1. Create the auth user (sends Supabase's built-in verification email).
      const { data: signUpData, error: signUpError } = await supabasePartner.auth.signUp({
        email: cleanEmail,
        password,
        options: { emailRedirectTo: window.location.origin + '/partner' },
      });

      if (signUpError) {
        const msg = (signUpError.message || '').toLowerCase();
        if (msg.includes('already registered') || msg.includes('already been registered')) {
          setSubmitError(dupMsg);
        } else {
          setSubmitError('Could not create your account. Please try again.');
        }
        setSubmitting(false);
        return;
      }

      // Supabase returns a user with an EMPTY identities array when the email is
      // already registered (anti-enumeration), instead of an error. Treat that
      // as a duplicate too.
      if (signUpData?.user && Array.isArray(signUpData.user.identities) && signUpData.user.identities.length === 0) {
        setSubmitError(dupMsg);
        setSubmitting(false);
        return;
      }

      const authUserId = signUpData?.user?.id;
      if (!authUserId) {
        setSubmitError('Could not create your account. Please try again.');
        setSubmitting(false);
        return;
      }

      // 2. Create the linked partner profile row via the edge function.
      const { data: regData, error: regError } = await supabasePartner.functions.invoke('partner-register', {
        body: {
          authUserId,
          companyName: companyName.trim(),
          contactPerson: contactPerson.trim(),
          email: cleanEmail,
          phone: phone.trim(),
          gstin: gstin.trim().toUpperCase(),
          gstLegalName: gstLegalName.trim(),
          gstAddress: gstAddress.trim(),
          pincode: pincode.trim(),
          state: state.trim(),
          stateCode,
        },
      });

      // functions.invoke surfaces non-2xx as an error; the JSON body (with our
      // message/code — incl. 409 email_exists) is still available on error.context.
      if (regError) {
        let message = 'Could not complete registration. Please try again.';
        try {
          const body = await regError.context?.json?.();
          if (body?.error) message = body.error;
        } catch (_) {
          /* fall back to the generic message */
        }
        setSubmitError(message);
        setSubmitting(false);
        return;
      }

      if (regData?.error) {
        setSubmitError(regData.error);
        setSubmitting(false);
        return;
      }

      // 3. Upload documents (NON-FATAL). The account + partner row already exist
      //    at this point, so a failed upload must not block email verification —
      //    it only surfaces a soft note on the confirmation screen.
      const partnerId = regData?.partnerId;
      const softNote = "We couldn't upload your documents just now — you can add them after signing in.";
      if (partnerId) {
        try {
          const { data: upData, error: upError } = await supabasePartner.functions.invoke('partner-upload', {
            body: {
              partnerId,
              gstCert: gstCertPayload,
              ...(tradeLicensePayload ? { tradeLicense: tradeLicensePayload } : {}),
            },
          });
          if (upError || upData?.error) {
            setUploadNote(softNote);
          }
        } catch (_) {
          setUploadNote(softNote);
        }
      } else {
        setUploadNote(softNote);
      }

      // 4. Success → check-email confirmation screen.
      setDone(true);
    } catch (err) {
      setSubmitError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  // ─── Footer links shown across every step / screen ──────────────────────────
  const FooterLinks = () => (
    <>
      <div className="flex items-center justify-center gap-1.5 mt-6">
        <span className="font-dm-sans text-sm text-white/55">Already a partner?</span>
        <Link
          to="/partner/login"
          className="font-dm-sans text-sm font-medium text-[#D4A843] hover:text-[#C9A84C] transition-colors duration-300"
        >
          Sign in
        </Link>
      </div>
      <div className="mt-4 pt-4 border-t border-white/[0.07] text-center">
        <Link
          to="/"
          className="font-dm-sans text-[13px] text-white/40 hover:text-[rgba(201,168,76,0.85)] transition-colors duration-300"
        >
          ← Back to main site
        </Link>
      </div>
    </>
  );

  // ─── Check-email confirmation screen ────────────────────────────────────────
  if (done) {
    return (
      <div className={cardWrap}>
        <div className={cardInner}>
          <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)] mb-2">
            Partner Portal
          </p>
          <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
            Almost there
          </h1>
          <p className="font-dm-sans text-sm text-white/65 font-light mt-3 leading-relaxed">
            We've sent a verification link to <span className="text-white">{email.trim().toLowerCase()}</span>. Click it to
            activate your account, then you'll be taken to your partner dashboard.
          </p>
          <p className="font-dm-sans text-[13px] text-white/45 font-light mt-3 leading-relaxed">
            It may take a minute to arrive. If you don't see it, please check your spam or promotions folder.
          </p>
          {uploadNote && (
            <p className="font-dm-sans text-[13px] text-[rgba(201,168,76,0.85)] font-light mt-4 leading-relaxed">
              {uploadNote}
            </p>
          )}
          <FooterLinks />
        </div>
      </div>
    );
  }

  // ─── Registration form ──────────────────────────────────────────────────────
  return (
    <div className={cardWrap}>
      <div className={cardInner}>

        <Stepper current={step} />

        <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)] mb-2">
          Partner Portal · Step {step} of 3
        </p>
        <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
          {step === 1 ? 'Create your account' : step === 2 ? 'GST details' : 'Upload your documents'}
        </h1>
        <p className="font-dm-sans text-sm text-white/55 font-light mb-7 leading-relaxed">
          {step === 1
            ? 'Tell us about your agency to get started'
            : step === 2
              ? 'GST registration is required for partner accounts'
              : 'These help us verify your business. Your documents are stored securely and reviewed by our team.'}
        </p>

        {step === 1 ? (
          <div className="flex flex-col gap-5">

            <div className="group flex flex-col gap-2">
              <label className={labelBase}>Company Name</label>
              <div className={borderBase}>
                <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} className={inputBase} autoComplete="organization" />
              </div>
              {errors.companyName ? (
                <p className={errText}>{errors.companyName}</p>
              ) : (
                <p className={hintText}>Your registered business or agency name</p>
              )}
            </div>

            <div className="group flex flex-col gap-2">
              <label className={labelBase}>Contact Person</label>
              <div className={borderBase}>
                <input type="text" value={contactPerson} onChange={e => setContactPerson(e.target.value)} className={inputBase} autoComplete="name" />
              </div>
              {errors.contactPerson ? (
                <p className={errText}>{errors.contactPerson}</p>
              ) : (
                <p className={hintText}>Full name of the primary contact</p>
              )}
            </div>

            <div className="group flex flex-col gap-2">
              <label className={labelBase}>Email</label>
              <div className={borderBase}>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputBase} autoComplete="email" />
              </div>
              {errors.email ? (
                <p className={errText}>{errors.email}</p>
              ) : (
                <p className={hintText}>You'll use this to log in — we'll send a verification link here</p>
              )}
            </div>

            <div className="group flex flex-col gap-2">
              <label className={labelBase}>Phone Number</label>
              <div className={borderBase}>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
                  className={inputBase}
                  autoComplete="tel"
                  inputMode="numeric"
                />
              </div>
              {errors.phone ? (
                <p className={errText}>{errors.phone}</p>
              ) : (
                <p className={hintText}>Your business contact number</p>
              )}
            </div>

            <div className="group flex flex-col gap-2">
              <label className={labelBase}>Password</label>
              <div className={borderBase}>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} className={inputBase} autoComplete="new-password" />
              </div>
              {errors.password ? (
                <p className={errText}>{errors.password}</p>
              ) : (
                <p className={hintText}>At least 6 characters.</p>
              )}
            </div>

            <div className="group flex flex-col gap-2">
              <label className={labelBase}>Confirm Password</label>
              <div className={borderBase}>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={inputBase} autoComplete="new-password" />
              </div>
              {errors.confirmPassword ? (
                <p className={errText}>{errors.confirmPassword}</p>
              ) : (
                <p className={hintText}>Re-enter your password</p>
              )}
            </div>

            <button type="button" onClick={handleNext} disabled={!step1Valid} className={`${primaryBtn} mt-3`}>
              Next
            </button>

          </div>
        ) : step === 2 ? (
          <div className="flex flex-col gap-5">

            <div className="group flex flex-col gap-2">
              <label className={labelBase}>GSTIN</label>
              <div className={borderBase}>
                <input
                  type="text"
                  value={gstin}
                  onChange={e => setGstin(e.target.value.toUpperCase())}
                  className={`${inputBase} tracking-[0.06em]`}
                  maxLength={15}
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                />
              </div>
              {errors.gstin ? (
                <p className={errText}>{errors.gstin}</p>
              ) : (
                <p className={hintText}>Your 15-character GST identification number</p>
              )}
            </div>

            <div className="group flex flex-col gap-2">
              <label className={labelBase}>GST Legal Name</label>
              <div className={borderBase}>
                <input type="text" value={gstLegalName} onChange={e => setGstLegalName(e.target.value)} className={inputBase} autoComplete="off" />
              </div>
              {errors.gstLegalName ? (
                <p className={errText}>{errors.gstLegalName}</p>
              ) : (
                <p className={hintText}>The legal name registered under your GSTIN (may differ from your trading name)</p>
              )}
            </div>

            <div className="group flex flex-col gap-2">
              <label className={labelBase}>GST Address</label>
              <div className={borderBase}>
                <textarea
                  value={gstAddress}
                  onChange={e => setGstAddress(e.target.value)}
                  rows={2}
                  className={`${inputBase} resize-none`}
                  autoComplete="off"
                />
              </div>
              {errors.gstAddress ? (
                <p className={errText}>{errors.gstAddress}</p>
              ) : (
                <p className={hintText}>Your registered business address as per GST</p>
              )}
            </div>

            <div className="group flex flex-col gap-2">
              <label className={labelBase}>Pincode</label>
              <div className={borderBase}>
                <input
                  type="text"
                  inputMode="numeric"
                  value={pincode}
                  onChange={e => setPincode(e.target.value.replace(/\D/g, ''))}
                  className={inputBase}
                  maxLength={6}
                  autoComplete="off"
                />
              </div>
              {errors.pincode ? (
                <p className={errText}>{errors.pincode}</p>
              ) : (
                <p className={hintText}>The 6-digit pincode of your GST-registered address</p>
              )}
            </div>

            <div className="group flex flex-col gap-2">
              <label className={labelBase}>State</label>
              <div className={`relative ${borderBase}`}>
                <select
                  value={state}
                  onChange={e => setState(e.target.value)}
                  className={`${inputBase} appearance-none pr-5 cursor-pointer bg-[#0D1E35] ${state ? 'text-white' : 'text-white/55'}`}
                >
                  <option value="" disabled>Select state</option>
                  {STATES.map(s => (
                    <option key={s.code} value={s.name}>{s.name} ({s.code})</option>
                  ))}
                </select>
                <span className="absolute right-0 bottom-3 pointer-events-none text-[rgba(212,168,67,0.5)] text-[10px] leading-none">&#9660;</span>
              </div>
              {errors.state ? (
                <p className={errText}>{errors.state}</p>
              ) : state ? (
                <p className={hintText}>GST state code: {stateCode}</p>
              ) : (
                <p className={hintText}>The state where your GST is registered</p>
              )}
            </div>

            <div className="flex items-stretch gap-3 mt-3">
              <button type="button" onClick={handleBack} className={ghostBtn}>
                Back
              </button>
              <button type="button" onClick={handleNext2} disabled={!step2Valid} className={`${primaryBtn} flex-1`}>
                Next
              </button>
            </div>

          </div>
        ) : (
          <div className="flex flex-col gap-5">

            <DocPicker
              label="GST Certificate"
              hint="Required · PDF, JPG, or PNG · max 3MB"
              file={gstCertFile}
              error={gstCertError}
              onSelect={handleGstCertSelect}
              onRemove={() => { setGstCertFile(null); setGstCertError(''); }}
            />

            <DocPicker
              label="Trade License"
              hint="Optional · PDF, JPG, or PNG · max 3MB"
              file={tradeLicenseFile}
              error={tradeLicenseError}
              onSelect={handleTradeLicenseSelect}
              onRemove={() => { setTradeLicenseFile(null); setTradeLicenseError(''); }}
            />

            <div className="flex items-stretch gap-3 mt-3">
              <button type="button" onClick={handleBack} disabled={submitting} className={ghostBtn}>
                Back
              </button>
              <button type="button" onClick={handleSubmit} disabled={!step3Valid || submitting} className={`${primaryBtn} flex-1`}>
                {submitting ? 'Creating account…' : 'Create account'}
              </button>
            </div>

          </div>
        )}

        {submitError && (
          <p className="font-dm-sans text-center text-sm text-white/55 mt-4 leading-relaxed">
            {submitError}
          </p>
        )}

        <FooterLinks />

      </div>
    </div>
  );
}
